/**
 * QR code encoder for the lock certificate (the verification code that leads back to the page).
 *
 * Adapted from "QR Code generator library (TypeScript)", Copyright (c) Project Nayuki, https://www.nayuki.io/page/qr-code-generator-library
 * (the same algorithm qrcode.react ships), reduced to what is needed here: byte mode (UTF-8 text), versions 1 to 40, the four error correction
 * levels with automatic boosting, automatic mask selection. No dependency.
 *
 * MIT License. Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated documentation files
 * (the "Software"), to deal in the Software without restriction, including without limitation the rights to use, copy, modify, merge, publish,
 * distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the
 * following conditions: the above copyright notice and this permission notice shall be included in all copies or substantial portions of the
 * Software. THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
 * MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY
 * CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
/* eslint-disable no-bitwise, class-methods-use-this */
export type EccLevel = 'L' | 'M' | 'Q' | 'H'
const ORDINAL: Record<EccLevel, number> = { L: 0, M: 1, Q: 2, H: 3 }
const FORMAT_BITS = [1, 0, 3, 2]
const ECC_LEVELS: EccLevel[] = ['L', 'M', 'Q', 'H']

const ECC_CODEWORDS_PER_BLOCK = [
  [-1, 7, 10, 15, 20, 26, 18, 20, 24, 30, 18, 20, 24, 26, 30, 22, 24, 28, 30, 28, 28, 28, 28, 30, 30, 26, 28, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30],
  [-1, 10, 16, 26, 18, 24, 16, 18, 22, 22, 26, 30, 22, 22, 24, 24, 28, 28, 26, 26, 26, 26, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28],
  [-1, 13, 22, 18, 26, 18, 24, 18, 22, 20, 24, 28, 26, 24, 20, 30, 24, 28, 28, 26, 30, 28, 30, 30, 30, 30, 28, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30],
  [-1, 17, 28, 22, 16, 22, 28, 26, 26, 24, 28, 24, 28, 22, 24, 24, 30, 28, 28, 26, 28, 30, 24, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30],
]
const NUM_ERROR_CORRECTION_BLOCKS = [
  [-1, 1, 1, 1, 1, 1, 2, 2, 2, 2, 4, 4, 4, 4, 4, 6, 6, 6, 6, 7, 8, 8, 9, 9, 10, 12, 12, 12, 13, 14, 15, 16, 17, 18, 19, 19, 20, 21, 22, 24, 25],
  [-1, 1, 1, 1, 2, 2, 4, 4, 4, 5, 5, 5, 8, 9, 9, 10, 10, 11, 13, 14, 16, 17, 17, 18, 20, 21, 23, 25, 26, 28, 29, 31, 33, 35, 37, 38, 40, 43, 45, 47, 49],
  [-1, 1, 1, 2, 2, 4, 4, 6, 6, 8, 8, 8, 10, 12, 16, 12, 17, 16, 18, 21, 20, 23, 23, 25, 27, 29, 34, 34, 35, 38, 40, 43, 45, 48, 51, 53, 56, 59, 62, 65, 68],
  [-1, 1, 1, 2, 4, 4, 4, 5, 6, 8, 8, 11, 11, 16, 16, 18, 16, 19, 21, 25, 25, 25, 34, 30, 32, 35, 37, 40, 42, 45, 48, 51, 54, 57, 60, 63, 66, 70, 74, 77, 81],
]

const getBit = (x: number, i: number) => ((x >>> i) & 1) !== 0

function appendBits(val: number, len: number, bb: number[]) {
  for (let i = len - 1; i >= 0; i--) bb.push((val >>> i) & 1)
}

const numRawDataModules = (ver: number) => {
  let result = (16 * ver + 128) * ver + 64
  if (ver >= 2) {
    const numAlign = Math.floor(ver / 7) + 2
    result -= (25 * numAlign - 10) * numAlign - 55
    if (ver >= 7) result -= 36
  }
  return result
}
const numDataCodewords = (ver: number, ecl: number) => Math.floor(numRawDataModules(ver) / 8) - ECC_CODEWORDS_PER_BLOCK[ecl][ver] * NUM_ERROR_CORRECTION_BLOCKS[ecl][ver]

function rsMultiply(x: number, y: number) {
  let z = 0
  for (let i = 7; i >= 0; i--) {
    z = (z << 1) ^ ((z >>> 7) * 0x11d)
    z ^= ((y >>> i) & 1) * x
  }
  return z
}
function rsDivisor(degree: number) {
  const result: number[] = []
  for (let i = 0; i < degree - 1; i++) result.push(0)
  result.push(1)
  let root = 1
  for (let i = 0; i < degree; i++) {
    for (let j = 0; j < result.length; j++) {
      result[j] = rsMultiply(result[j], root)
      if (j + 1 < result.length) result[j] ^= result[j + 1]
    }
    root = rsMultiply(root, 2)
  }
  return result
}
function rsRemainder(data: number[], divisor: number[]) {
  const result = divisor.map(() => 0)
  for (const b of data) {
    const factor = b ^ (result.shift() as number)
    result.push(0)
    divisor.forEach((coef, i) => {
      result[i] ^= rsMultiply(coef, factor)
    })
  }
  return result
}

class Grid {
  size: number

  modules: boolean[][] = []

  isFunction: boolean[][] = []

  constructor(public version: number, public ecl: number, dataCodewords: number[]) {
    this.size = version * 4 + 17
    for (let i = 0; i < this.size; i++) {
      this.modules.push(new Array(this.size).fill(false))
      this.isFunction.push(new Array(this.size).fill(false))
    }
    this.drawFunctionPatterns()
    this.drawCodewords(this.addEccAndInterleave(dataCodewords))
    let best = 0
    let minPenalty = 1e9
    for (let m = 0; m < 8; m++) {
      this.applyMask(m)
      this.drawFormatBits(m)
      const penalty = this.penaltyScore()
      if (penalty < minPenalty) {
        best = m
        minPenalty = penalty
      }
      this.applyMask(m) // undo
    }
    this.applyMask(best)
    this.drawFormatBits(best)
  }

  private setFunction(x: number, y: number, dark: boolean) {
    this.modules[y][x] = dark
    this.isFunction[y][x] = true
  }

  private drawFunctionPatterns() {
    for (let i = 0; i < this.size; i++) {
      this.setFunction(6, i, i % 2 === 0)
      this.setFunction(i, 6, i % 2 === 0)
    }
    this.drawFinder(3, 3)
    this.drawFinder(this.size - 4, 3)
    this.drawFinder(3, this.size - 4)
    const pos = this.alignmentPositions()
    const n = pos.length
    for (let i = 0; i < n; i++) {
      for (let j = 0; j < n; j++) {
        if (!((i === 0 && j === 0) || (i === 0 && j === n - 1) || (i === n - 1 && j === 0))) this.drawAlignment(pos[i], pos[j])
      }
    }
    this.drawFormatBits(0)
    this.drawVersion()
  }

  private drawFormatBits(mask: number) {
    const data = (FORMAT_BITS[this.ecl] << 3) | mask
    let rem = data
    for (let i = 0; i < 10; i++) rem = (rem << 1) ^ ((rem >>> 9) * 0x537)
    const bits = ((data << 10) | rem) ^ 0x5412
    for (let i = 0; i <= 5; i++) this.setFunction(8, i, getBit(bits, i))
    this.setFunction(8, 7, getBit(bits, 6))
    this.setFunction(8, 8, getBit(bits, 7))
    this.setFunction(7, 8, getBit(bits, 8))
    for (let i = 9; i < 15; i++) this.setFunction(14 - i, 8, getBit(bits, i))
    for (let i = 0; i < 8; i++) this.setFunction(this.size - 1 - i, 8, getBit(bits, i))
    for (let i = 8; i < 15; i++) this.setFunction(8, this.size - 15 + i, getBit(bits, i))
    this.setFunction(8, this.size - 8, true)
  }

  private drawVersion() {
    if (this.version < 7) return
    let rem = this.version
    for (let i = 0; i < 12; i++) rem = (rem << 1) ^ ((rem >>> 11) * 0x1f25)
    const bits = (this.version << 12) | rem
    for (let i = 0; i < 18; i++) {
      const dark = getBit(bits, i)
      const a = this.size - 11 + (i % 3)
      const b = Math.floor(i / 3)
      this.setFunction(a, b, dark)
      this.setFunction(b, a, dark)
    }
  }

  private drawFinder(x: number, y: number) {
    for (let dy = -4; dy <= 4; dy++) {
      for (let dx = -4; dx <= 4; dx++) {
        const dist = Math.max(Math.abs(dx), Math.abs(dy))
        const xx = x + dx
        const yy = y + dy
        if (xx >= 0 && xx < this.size && yy >= 0 && yy < this.size) this.setFunction(xx, yy, dist !== 2 && dist !== 4)
      }
    }
  }

  private drawAlignment(x: number, y: number) {
    for (let dy = -2; dy <= 2; dy++) for (let dx = -2; dx <= 2; dx++) this.setFunction(x + dx, y + dy, Math.max(Math.abs(dx), Math.abs(dy)) !== 1)
  }

  private alignmentPositions(): number[] {
    if (this.version === 1) return []
    const numAlign = Math.floor(this.version / 7) + 2
    const step = this.version === 32 ? 26 : Math.ceil((this.version * 4 + 4) / (numAlign * 2 - 2)) * 2
    const result = [6]
    for (let pos = this.size - 7; result.length < numAlign; pos -= step) result.splice(1, 0, pos)
    return result
  }

  private addEccAndInterleave(data: number[]): number[] {
    const { version: ver, ecl } = this
    const numBlocks = NUM_ERROR_CORRECTION_BLOCKS[ecl][ver]
    const blockEccLen = ECC_CODEWORDS_PER_BLOCK[ecl][ver]
    const rawCodewords = Math.floor(numRawDataModules(ver) / 8)
    const numShortBlocks = numBlocks - (rawCodewords % numBlocks)
    const shortBlockLen = Math.floor(rawCodewords / numBlocks)
    const blocks: number[][] = []
    const div = rsDivisor(blockEccLen)
    for (let i = 0, k = 0; i < numBlocks; i++) {
      const dat = data.slice(k, k + shortBlockLen - blockEccLen + (i < numShortBlocks ? 0 : 1))
      k += dat.length
      const ecc = rsRemainder(dat, div)
      if (i < numShortBlocks) dat.push(0)
      blocks.push(dat.concat(ecc))
    }
    const result: number[] = []
    for (let i = 0; i < blocks[0].length; i++) {
      blocks.forEach((block, j) => {
        if (i !== shortBlockLen - blockEccLen || j >= numShortBlocks) result.push(block[i])
      })
    }
    return result
  }

  private drawCodewords(data: number[]) {
    let i = 0
    for (let right = this.size - 1; right >= 1; right -= 2) {
      if (right === 6) right = 5
      for (let vert = 0; vert < this.size; vert++) {
        for (let j = 0; j < 2; j++) {
          const x = right - j
          const upward = ((right + 1) & 2) === 0
          const y = upward ? this.size - 1 - vert : vert
          if (!this.isFunction[y][x] && i < data.length * 8) {
            this.modules[y][x] = getBit(data[i >>> 3], 7 - (i & 7))
            i++
          }
        }
      }
    }
  }

  private applyMask(mask: number) {
    for (let y = 0; y < this.size; y++) {
      for (let x = 0; x < this.size; x++) {
        let invert: boolean
        switch (mask) {
          case 0: invert = (x + y) % 2 === 0; break
          case 1: invert = y % 2 === 0; break
          case 2: invert = x % 3 === 0; break
          case 3: invert = (x + y) % 3 === 0; break
          case 4: invert = (Math.floor(x / 3) + Math.floor(y / 2)) % 2 === 0; break
          case 5: invert = ((x * y) % 2) + ((x * y) % 3) === 0; break
          case 6: invert = (((x * y) % 2) + ((x * y) % 3)) % 2 === 0; break
          default: invert = (((x + y) % 2) + ((x * y) % 3)) % 2 === 0
        }
        if (!this.isFunction[y][x] && invert) this.modules[y][x] = !this.modules[y][x]
      }
    }
  }

  private penaltyScore(): number {
    let result = 0
    const size = this.size
    const run = (get: (a: number, b: number) => boolean) => {
      for (let a = 0; a < size; a++) {
        let runColor = false
        let runLen = 0
        const history = [0, 0, 0, 0, 0, 0, 0]
        for (let b = 0; b < size; b++) {
          if (get(a, b) === runColor) {
            runLen++
            if (runLen === 5) result += 3
            else if (runLen > 5) result++
          } else {
            this.finderAdd(runLen, history)
            if (!runColor) result += this.finderCount(history) * 40
            runColor = get(a, b)
            runLen = 1
          }
        }
        result += this.finderTerminate(runColor, runLen, history) * 40
      }
    }
    run((y, x) => this.modules[y][x])
    run((x, y) => this.modules[y][x])
    for (let y = 0; y < size - 1; y++) {
      for (let x = 0; x < size - 1; x++) {
        const c = this.modules[y][x]
        if (c === this.modules[y][x + 1] && c === this.modules[y + 1][x] && c === this.modules[y + 1][x + 1]) result += 3
      }
    }
    let dark = 0
    this.modules.forEach((row) => row.forEach((c) => { if (c) dark++ }))
    const total = size * size
    result += (Math.ceil(Math.abs(dark * 20 - total * 10) / total) - 1) * 10
    return result
  }

  private finderCount(h: number[]): number {
    const n = h[1]
    const core = n > 0 && h[2] === n && h[3] === n * 3 && h[4] === n && h[5] === n
    return (core && h[0] >= n * 4 && h[6] >= n ? 1 : 0) + (core && h[6] >= n * 4 && h[0] >= n ? 1 : 0)
  }

  private finderTerminate(color: boolean, len: number, h: number[]): number {
    let l = len
    if (color) {
      this.finderAdd(l, h)
      l = 0
    }
    l += this.size
    this.finderAdd(l, h)
    return this.finderCount(h)
  }

  private finderAdd(len: number, h: number[]) {
    let l = len
    if (h[0] === 0) l += this.size
    h.pop()
    h.unshift(l)
  }
}

/**
 * The modules of the QR code for `text` (true = dark), row by row, without the quiet zone. `boost` raises the error correction level while the
 * data still fits in the same version, exactly like qrcode.react does.
 */
export function qrModules(text: string, level: EccLevel = 'M', boost = true): boolean[][] {
  const bytes = Array.from(new TextEncoder().encode(text))
  let ecl = ORDINAL[level]
  let version = 1
  let usedBits = 0
  for (; ; version++) {
    const countBits = version <= 9 ? 8 : 16
    usedBits = 4 + countBits + bytes.length * 8
    if (bytes.length >= 1 << countBits) {
      if (version >= 40) throw new RangeError('Data too long')
      continue
    }
    if (usedBits <= numDataCodewords(version, ecl) * 8) break
    if (version >= 40) throw new RangeError('Data too long')
  }
  ;[1, 2, 3].forEach((o) => {
    if (boost && usedBits <= numDataCodewords(version, o) * 8) ecl = o
  })
  const bb: number[] = []
  appendBits(0x4, 4, bb)
  appendBits(bytes.length, version <= 9 ? 8 : 16, bb)
  bytes.forEach((b) => appendBits(b, 8, bb))
  const capacity = numDataCodewords(version, ecl) * 8
  appendBits(0, Math.min(4, capacity - bb.length), bb)
  appendBits(0, (8 - (bb.length % 8)) % 8, bb)
  for (let pad = 0xec; bb.length < capacity; pad ^= 0xec ^ 0x11) appendBits(pad, 8, bb)
  const codewords: number[] = new Array(bb.length / 8).fill(0)
  bb.forEach((b, i) => {
    codewords[i >>> 3] |= b << (7 - (i & 7))
  })
  return new Grid(version, ecl, codewords).modules
}

export const eccLevelUsed = (text: string, level: EccLevel = 'M'): EccLevel => {
  const n = new TextEncoder().encode(text).length
  let version = 1
  for (; version < 40; version++) if (4 + (version <= 9 ? 8 : 16) + n * 8 <= numDataCodewords(version, ORDINAL[level]) * 8) break
  let best = ORDINAL[level]
  ;[1, 2, 3].forEach((o) => {
    if (4 + (version <= 9 ? 8 : 16) + n * 8 <= numDataCodewords(version, o) * 8) best = o
  })
  return ECC_LEVELS[best]
}

/**
 * The code as one SVG path (every dark module is a 1x1 square `M x y h1 v1 h-1 z`), with a quiet zone of `border` modules, plus the viewBox size.
 * Drawn as a single path so it stays crisp at any size and weighs a few kilobytes.
 */
export function qrPath(text: string, border = 2, level: EccLevel = 'M'): { d: string; size: number } {
  const m = qrModules(text, level)
  const parts: string[] = []
  m.forEach((row, y) => {
    let x = 0
    while (x < row.length) {
      if (!row[x]) {
        x++
        continue
      }
      let w = 1
      while (x + w < row.length && row[x + w]) w++
      parts.push(`M${x + border} ${y + border}h${w}v1h-${w}z`)
      x += w
    }
  })
  return { d: parts.join(''), size: m.length + border * 2 }
}

/** The code as a complete SVG document (for an <img> data URI, the PNG and the certificate), dark modules `fg` on `bg`. */
export function qrSvg(text: string, opts: { fg?: string; bg?: string; border?: number; level?: EccLevel } = {}): string {
  const { d, size } = qrPath(text, opts.border ?? 2, opts.level ?? 'M')
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${size} ${size}" shape-rendering="crispEdges"><rect width="${size}" height="${size}" fill="${opts.bg ?? '#ffffff'}"/><path d="${d}" fill="${opts.fg ?? '#000000'}"/></svg>`
}
